import { Suspense, useLayoutEffect, useMemo, useRef, useState } from "react";

import { type ITelemetryCustom, sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector, useLikedContext, useViewportInfoContext } from "@/src/contexts";
import { TelemetryEntity, jumpToSectionId, refineURL } from "@Components/Modules/Helpers";
import type { ITextbox } from "@Components/Textbox";
import { Textbox, TextboxAppearance } from "@Components/Textbox";
import { allStoresPanelId, scrollToCbBrandsList } from "@Constants/PortalConstants";
import * as stylex from "@stylexjs/stylex";

import { type QueryXsMax, colors } from "../../../styles/tokens.stylex";
import PanelSection from "../../shared/panel-section";
import CashbackCategories from "../CashbackCategories";
import type { ICashbackBrandTile, ICashbackBrandsByCategory } from "../CashbackCommonInterfaces";
import CashbackBrandsByCategory from "./CashbackBrandsByCategory";
import { CashbackMainPageResources } from "./CashbackMainPage.Resources";
import { CashbackMainPageResourcesEn } from "./CashbackMainPage.Resources.En";
import FavoriteCashbackBrands from "./FavoriteCashbackBrands";

import * as css from "./BrandsCashbackSection.module.css";

export interface IBrandsCashbackSection {
    initialData: IBrandsCashbackSectionResult;
}

export type ICashbackBrandsTileDictionary = { [key: string]: ICashbackBrandTile };

export interface IBrandsCashbackSectionResult {
    cashbackBrandsByCategories: ICashbackBrandsByCategory[];
    allBrands: ICashbackBrandsTileDictionary;
}

const keyCategoryAll = "00000000-0000-0000-0000-000000000000";

export default function BrandsCashbackSection({ initialData }: IBrandsCashbackSection) {
    const { isMobile, isTablet } = useViewportInfoContext();
    const resources = languageSelector(
        CashbackMainPageResources.brandsCashbackSection,
        CashbackMainPageResourcesEn.brandsCashbackSection,
    );
    const [brandIds] = useLikedContext("brands");

    const [searchText, setSearchText] = useState("");
    const [categoryKey, setCategoryKey] = useState(keyCategoryAll);

    const textboxRef = useRef<HTMLInputElement>(null);

    const viewTelemetry = (eventName: string) =>
        ({
            eventName: `${TelemetryEntity.Cashback}-${eventName}`,
            actionType: "View",
        }) satisfies ITelemetryCustom;
    const clickTelemetry = (eventName: string, id?: string) =>
        ({
            eventName: `${TelemetryEntity.Cashback}-${eventName}`,
            actionType: "Click",
            itemId: id,
        }) satisfies ITelemetryCustom;

    useLayoutEffect(() => {
        // TODO: Implement Intersection Observer, in case targeted element moves inside DOM
        const timer = setTimeout(() => {
            if (
                window.location.href.indexOf(scrollToCbBrandsList) > -1 &&
                document.getElementById(allStoresPanelId)
            ) {
                jumpToSectionId(allStoresPanelId);
                refineURL();
            }
        }, 500);

        return () => clearTimeout(timer);
    }, []);

    const filterOnSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = (event.target && event.target.value) || "";
        setSearchText(value);
    };

    const searchTextBox: ITextbox = {
        appearance: TextboxAppearance.SearchGlobal,
        watermarkText: resources.searchText,
        title: resources.searchText,
        onChange: filterOnSearch,
        onFocus: () => sendTelemetryEvent(clickTelemetry("SearchEdit")),
    };

    const onChangeCategory = (categoryId: string) => {
        setCategoryKey(categoryId);
        setSearchText("");

        if (textboxRef.current?.value) {
            textboxRef.current.value = "";
        }

        sendTelemetryEvent(clickTelemetry(TelemetryEntity.Category, categoryKey));
    };

    const orderFavorites = useMemo(() => {
        if (!initialData?.allBrands) {
            return Array.from<ICashbackBrandTile>({ length: isMobile ? 2 : isTablet ? 4 : 6 });
        }

        const allBrandsIds = initialData.cashbackBrandsByCategories.find(
            (c) => c.categoryKey === keyCategoryAll,
        ).initialData;

        const brandsFiltered: ICashbackBrandTile[] = [];
        for (let i = 0; i < allBrandsIds.length; i += 1) {
            const brand = initialData.allBrands[allBrandsIds[i]];
            if (brandIds[brand.id] === true) {
                brandsFiltered.push(brand);
            }
        }

        return brandsFiltered;
    }, [initialData, brandIds, isMobile, isTablet]);

    const selectedBrands: ICashbackBrandTile[] = useMemo(() => {
        const brands: ICashbackBrandTile[] = [];

        if (!initialData?.allBrands) {
            return brands;
        }

        let pushMethod = (brand: ICashbackBrandTile) => {
            brands.push(brand);
        };

        if (searchText !== "") {
            const lSearchText = searchText.toLocaleLowerCase();
            pushMethod = (brand: ICashbackBrandTile) => {
                if (brand.description?.toLocaleLowerCase().includes(lSearchText)) {
                    brands.push(brand);
                }
            };
        }

        initialData.cashbackBrandsByCategories
            .find((c) => c.categoryKey === categoryKey)
            ?.initialData.forEach((id) => pushMethod(initialData?.allBrands[id]));

        return brands ?? Array.from({ length: 8 });
    }, [categoryKey, initialData, searchText]);

    return (
        <Suspense>
            <PanelSection
                styleX={styles.favorites}
                title={resources.titleFavorites}
                telemetryCustom={viewTelemetry("FavoriteCashbackBrands")}
                hasLeftHeader
                initialInView
            >
                <FavoriteCashbackBrands
                    favoriteList={orderFavorites}
                    favoriteBtnScrollToId={allStoresPanelId}
                    addMoreFavText={resources.addMoreFavoritesText}
                />
            </PanelSection>
            <PanelSection
                id={allStoresPanelId}
                className={css.cashbackBrandsByCategorySectionComponent}
                telemetryCustom={viewTelemetry("CashbackStores")}
                initialInView
            >
                <div className={css.headerBrands}>
                    <h2>{resources.titleBrands}</h2>
                    <Textbox
                        {...searchTextBox}
                        forwardRef={textboxRef}
                        aria-label={searchTextBox.watermarkText}
                    />
                </div>
                <CashbackCategories
                    initialCategoryId={categoryKey}
                    onChangeCategory={onChangeCategory}
                    allCategory
                />
                <CashbackBrandsByCategory brandsCashback={selectedBrands} activeCategoryId={categoryKey} />
            </PanelSection>
        </Suspense>
    );
}

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    favorites: {
        backgroundColor: colors.bg_Primary,
        marginBottom: 0,
        paddingBottom: {
            default: "50px",
            [xsMax]: "40px",
        },
    },
});
